import type { ComponentPropsWithoutRef } from "react"
import { forwardRef } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { cn } from "@productlane/lib/src/cn"
import { Avatar, SlackIcon } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { api } from "@/lib/api"
import { useSlackConnections } from "@/lib/zero"
import { OriginBadge } from "./origin-badge"
import { resolveSlackOrigin, slackOriginPhrase } from "./slack-origin"

const SLACK_TEAMS_CACHE_MS = 60 * 60_000

type SlackOrigin = NonNullable<ReturnType<typeof resolveSlackOrigin>>

/** The Slack workspace a thread came from, or null while only one is connected. */
function useSlackOrigin(slackChannelId: string | null | undefined) {
	const connections = useSlackConnections()
	const isMultiWorkspace = connections.length > 1
	const [channel] = useQuery(
		queries.slackChannelByChannelId({ channelId: slackChannelId ?? "" }),
		{ enabled: isMultiWorkspace && Boolean(slackChannelId), ttl: "10m" },
	)
	const teams = api.workspace.slackTeams.useQuery(undefined, {
		enabled: isMultiWorkspace,
		staleTime: SLACK_TEAMS_CACHE_MS,
		cacheTime: SLACK_TEAMS_CACHE_MS,
	})
	return resolveSlackOrigin({ connections, channel, teams: teams.data })
}

/**
 * The workspace's logo with the Slack mark on its corner, filling the same box as the plain Slack icon.
 * Forwards ref and props so a Tooltip trigger (`asChild`) can attach its handlers.
 */
export const SlackWorkspaceStamp = forwardRef<
	HTMLSpanElement,
	ComponentPropsWithoutRef<"span"> & { origin: SlackOrigin; size: "sm" | "md" }
>(function SlackWorkspaceStamp({ origin, size, className, ...props }, ref) {
	const isMedium = size === "md"
	return (
		<span
			ref={ref}
			{...props}
			className={cn(
				"relative flex flex-none",
				isMedium ? "size-4" : "size-3",
				className,
			)}
		>
			<Avatar
				name={origin.name}
				image={origin.iconUrl ?? undefined}
				size="xxs"
				roundness="sm"
				className={cn(!isMedium && "!size-3 !min-h-3 !min-w-3 !text-[5px]")}
			/>
			<span
				className={cn(
					"absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-white dark:bg-gray-900",
					isMedium ? "size-2.5" : "size-2",
				)}
			>
				<SlackIcon
					className={cn("flex-none", isMedium ? "size-2" : "size-1.5")}
				/>
			</span>
		</span>
	)
})

/** The thread top bar's origin icon for a Slack thread, stamped with its workspace like the row. */
export function SlackThreadOriginBadge({
	slackChannelId,
	createdAt,
	updatedAt,
	className,
}: {
	slackChannelId: string | null | undefined
	createdAt?: number | Date | string
	updatedAt?: number | Date | string
	className?: string
}) {
	const origin = useSlackOrigin(slackChannelId)

	return (
		<OriginBadge
			origin="SLACK_CONNECT"
			createdAt={createdAt}
			updatedAt={updatedAt}
			className={className}
			phrase={slackOriginPhrase(origin)}
			icon={
				origin ? <SlackWorkspaceStamp origin={origin} size="md" /> : undefined
			}
		/>
	)
}
